import {
  AlertTriangle, FileSpreadsheet, Pencil, Plus, Search, SlidersHorizontal, Trash2,
  UserRound, Users,
} from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { atualizar, criar, eliminar, obter, paraQuery } from '../api';
import { useAuth } from '../auth';
import {
  Aviso, Carregando, Confirmacao, Erro, ListaComNovo, Modal, Spinner, Vazio,
} from '../componentes/base';
import { exportarFuncionariosExcel } from '../exportar';
import { dataCurta, euros, nib, numero } from '../formatos';
import type {
  AvisosFuncionarios, Empresa, EstadoCartao, Funcionario, Referencia,
} from '../tipos';

interface Resposta {
  funcionarios: Funcionario[];
  total: number;
  total_geral: number;
  avisos: AvisosFuncionarios;
}

const FILTROS_VAZIOS = { empresa_id: '', funcao: '', cartao: '' };

export default function Funcionarios() {
  const { utilizador } = useAuth();
  const eAdmin = utilizador?.papel === 'admin';

  const [resposta, setResposta] = useState<Resposta | null>(null);
  const [referencia, setReferencia] = useState<Referencia | null>(null);
  const [procura, setProcura] = useState('');
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [nota, setNota] = useState<string | null>(null);
  const [aCarregar, setACarregar] = useState(true);

  const [formAberto, setFormAberto] = useState(false);
  const [aEditar, setAEditar] = useState<Funcionario | null>(null);
  const [aEliminar, setAEliminar] = useState<Funcionario | null>(null);
  const [aProcessar, setAProcessar] = useState(false);

  const carregar = useCallback(() => {
    setACarregar(true);
    setErro(null);
    obter<Resposta>(`/funcionarios${paraQuery({
      procura, ...filtros, ativo: mostrarInativos ? undefined : 'true',
    })}`)
      .then(setResposta)
      .catch((e) => setErro(e.message))
      .finally(() => setACarregar(false));
  }, [procura, filtros, mostrarInativos]);

  useEffect(() => {
    // Debounce so typing in the search box does not fire a request per key.
    const temporizador = setTimeout(carregar, procura ? 300 : 0);
    return () => clearTimeout(temporizador);
  }, [carregar, procura]);

  useEffect(() => {
    obter<Referencia>('/referencia').then(setReferencia).catch(() => undefined);
  }, []);

  async function apagar() {
    if (!aEliminar) return;
    setAProcessar(true);
    try {
      const r = await eliminar<{ desativado: boolean; mensagem?: string }>(`/funcionarios/${aEliminar.id}`);
      setNota(r.mensagem ?? null);
      setAEliminar(null);
      carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível eliminar.');
    } finally {
      setAProcessar(false);
    }
  }

  const lista = resposta?.funcionarios ?? [];
  const avisos = resposta?.avisos;
  // Expired cards plus the ones about to expire: both need someone to act, and
  // splitting them into two badges just buries the number.
  const nAvisos = (avisos?.cartoes_expirados ?? 0) + (avisos?.cartoes_a_expirar ?? 0);
  const filtrosAtivos = Object.values(filtros).filter(Boolean).length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Users className="h-6 w-6 text-marca-600 dark:text-marca-400" aria-hidden />
          <div>
            <h1 className="text-xl font-semibold">Lista de Funcionários</h1>
            {resposta && (
              <p className="text-sm text-tinta-500 dark:text-tinta-400">
                {numero(resposta.total)} de {numero(resposta.total_geral)}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 sem-impressao">
          {eAdmin && (
            <button
              type="button" className="btn-primario"
              onClick={() => { setAEditar(null); setFormAberto(true); }}
            >
              <Plus className="h-4 w-4" aria-hidden />Adicionar Funcionário
            </button>
          )}
          <button
            type="button" className="btn bg-emerald-600 text-white hover:bg-emerald-700"
            disabled={!lista.length} onClick={() => exportarFuncionariosExcel(lista)}
          >
            <FileSpreadsheet className="h-4 w-4" aria-hidden />Exportar Excel
          </button>
          <button
            type="button"
            className="btn-secundario relative"
            aria-expanded={filtrosAbertos}
            onClick={() => setFiltrosAbertos((a) => !a)}
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden />
            Filtros
            {filtrosAtivos > 0 && (
              <span className="rounded-full bg-marca-600 px-1.5 text-xs font-semibold text-white">
                {filtrosAtivos}
              </span>
            )}
            {nAvisos > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 text-xs
                           font-semibold text-amber-900 dark:bg-amber-950 dark:text-amber-200"
                title={`${avisos?.cartoes_expirados ?? 0} cartão(ões) expirado(s), ${avisos?.cartoes_a_expirar ?? 0} a expirar nos próximos 30 dias`}
              >
                <AlertTriangle className="h-3 w-3" aria-hidden />{nAvisos}
              </span>
            )}
          </button>
        </div>
      </div>

      {nota && <Aviso>{nota}</Aviso>}
      {erro && <Erro mensagem={erro} aoTentar={carregar} />}

      <div className="cartao space-y-4 p-4 sem-impressao">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[240px] flex-1">
            <label htmlFor="procura" className="sr-only">Pesquisar</label>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tinta-400" aria-hidden />
            <input
              id="procura" className="campo pl-9"
              placeholder="Pesquisar por nome, email, NIF, cartão ou fornecedor…"
              value={procura} onChange={(e) => setProcura(e.target.value)}
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-tinta-700 dark:text-papel-300">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-papel-300 text-marca-600 focus:ring-marca-500"
              checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)}
            />
            Mostrar inativos
          </label>
        </div>

        {filtrosAbertos && (
          <div className="grid gap-3 border-t border-papel-200 pt-4 dark:border-tinta-700 md:grid-cols-3">
            <div>
              <label htmlFor="f-empresa" className="rotulo">Fornecedor</label>
              <select
                id="f-empresa" className="campo" value={filtros.empresa_id}
                onChange={(e) => setFiltros((f) => ({ ...f, empresa_id: e.target.value }))}
              >
                <option value="">Todos os fornecedores</option>
                {referencia?.empresas.map((e) => (
                  <option key={e.id} value={e.id}>{e.nome}</option>
                ))}
                <option value="sem-empresa">— Sem fornecedor —</option>
              </select>
            </div>
            <div>
              <label htmlFor="f-funcao-lista" className="rotulo">Função</label>
              <select
                id="f-funcao-lista" className="campo" value={filtros.funcao}
                onChange={(e) => setFiltros((f) => ({ ...f, funcao: e.target.value }))}
              >
                <option value="">Todas as funções</option>
                {referencia?.funcoes.map((f) => (
                  <option key={f.codigo} value={f.codigo}>{f.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="f-cartao" className="rotulo">Cartão de ID</label>
              <select
                id="f-cartao" className="campo" value={filtros.cartao}
                onChange={(e) => setFiltros((f) => ({ ...f, cartao: e.target.value }))}
              >
                <option value="">Todos</option>
                <option value="problema">Expirados ou a expirar ({nAvisos})</option>
                <option value="expirado">Expirados ({avisos?.cartoes_expirados ?? 0})</option>
                <option value="a_expirar">A expirar em 30 dias ({avisos?.cartoes_a_expirar ?? 0})</option>
                <option value="sem_data">Sem validade registada ({avisos?.cartoes_sem_data ?? 0})</option>
              </select>
            </div>
            {filtrosAtivos > 0 && (
              <button
                type="button"
                className="justify-self-start text-sm font-medium text-marca-600 hover:underline dark:text-marca-400"
                onClick={() => setFiltros(FILTROS_VAZIOS)}
              >
                Limpar filtros
              </button>
            )}
          </div>
        )}
      </div>

      <div className="cartao overflow-hidden">
        {aCarregar ? (
          <Carregando />
        ) : !lista.length ? (
          <Vazio
            titulo="Nenhum funcionário encontrado"
            descricao={procura || filtrosAtivos
              ? 'Tente outra pesquisa ou limpe os filtros.'
              : 'Comece por adicionar a sua equipa.'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-papel-50 text-left text-[11px] uppercase tracking-wide text-tinta-500 dark:bg-tinta-700/50 dark:text-tinta-400">
                <tr>
                  <th className="px-4 py-3 font-medium">Funcionário</th>
                  <th className="px-4 py-3 font-medium">Função</th>
                  <th className="px-4 py-3 font-medium">Contacto</th>
                  <th className="px-4 py-3 font-medium">Cartão de ID</th>
                  <th className="px-4 py-3 font-medium">NIF e NIB</th>
                  <th className="px-4 py-3 font-medium">Observações</th>
                  {eAdmin && <th className="px-4 py-3 text-right font-medium sem-impressao">Ações</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-papel-200 dark:divide-tinta-700">
                {lista.map((f) => (
                  <tr key={f.id} className="align-top hover:bg-papel-50 dark:hover:bg-tinta-700/40">
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2.5">
                        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-marca-100 text-marca-700 dark:bg-marca-900 dark:text-marca-200">
                          <UserRound className="h-4 w-4" aria-hidden />
                        </span>
                        <div>
                          <p className="font-medium">
                            {f.nome}
                            {!f.ativo && (
                              <span className="ml-2 rounded-full bg-papel-200 px-2 py-0.5 text-[11px] font-medium text-tinta-600 dark:bg-tinta-700 dark:text-tinta-300">
                                Inativo
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-tinta-500 dark:text-tinta-400">
                            {f.empresa_nome ?? 'Sem fornecedor'}
                          </p>
                          {f.data_nascimento && (
                            <p className="text-xs tabular-nums text-tinta-400">
                              Nascimento: {dataCurta(f.data_nascimento, true)}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <span className="inline-flex rounded-full bg-marca-50 px-2 py-0.5 text-xs font-medium text-marca-800 dark:bg-marca-950 dark:text-marca-200">
                        {f.funcao_nome ?? f.funcao}
                      </span>
                      <p className="mt-1 text-xs tabular-nums text-tinta-500 dark:text-tinta-400">
                        {f.custo_hora === null
                          ? <span className="text-amber-600 dark:text-amber-400">valor/hora por definir</span>
                          : `${euros(f.custo_hora)}/h`}
                      </p>
                    </td>

                    <td className="px-4 py-3 text-tinta-600 dark:text-papel-300">
                      {f.email && <p className="break-all">{f.email}</p>}
                      {f.telefone && <p className="text-xs text-tinta-500">{f.telefone}</p>}
                      {f.data_admissao && (
                        <p className="text-xs tabular-nums text-tinta-400">
                          Admissão: {dataCurta(f.data_admissao, true)}
                        </p>
                      )}
                      {!f.email && !f.telefone && !f.data_admissao && '—'}
                    </td>

                    <td className="px-4 py-3">
                      <CelulaCartao funcionario={f} />
                    </td>

                    <td className="px-4 py-3 text-tinta-600 dark:text-papel-300">
                      <p className="tabular-nums">{f.nif ? `NIF ${f.nif}` : '—'}</p>
                      {f.nib && (
                        <p className="text-xs tabular-nums text-tinta-500">NIB {nib(f.nib)}</p>
                      )}
                    </td>

                    <td className="max-w-[16rem] px-4 py-3 text-xs text-tinta-500 dark:text-tinta-400">
                      {f.notas || '—'}
                    </td>

                    {eAdmin && (
                      <td className="px-4 py-3 text-right sem-impressao">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            className="rounded-lg p-1.5 text-marca-600 hover:bg-marca-50 dark:text-marca-400 dark:hover:bg-tinta-700"
                            onClick={() => { setAEditar(f); setFormAberto(true); }}
                            aria-label={`Editar ${f.nome}`}
                          >
                            <Pencil className="h-4 w-4" aria-hidden />
                          </button>
                          <button
                            type="button"
                            className="rounded-lg p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-tinta-700"
                            onClick={() => setAEliminar(f)}
                            aria-label={`Eliminar ${f.nome}`}
                          >
                            <Trash2 className="h-4 w-4" aria-hidden />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formAberto && referencia && (
        <FormularioFuncionario
          referencia={referencia}
          funcionario={aEditar}
          aoFechar={() => setFormAberto(false)}
          aoGuardar={() => { setFormAberto(false); carregar(); }}
          aoCriarEmpresa={(empresa) => setReferencia((r) => (r ? {
            ...r,
            empresas: [...r.empresas, empresa].sort((a, b) => a.nome.localeCompare(b.nome, 'pt')),
          } : r))}
        />
      )}

      <Confirmacao
        aberto={Boolean(aEliminar)}
        titulo="Eliminar funcionário"
        mensagem={aEliminar
          ? `Eliminar ${aEliminar.nome}? Se tiver horas registadas será desativado em vez de eliminado, para não perder o histórico.`
          : ''}
        aCarregar={aProcessar}
        aoConfirmar={apagar}
        aoCancelar={() => setAEliminar(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The card cell carries the whole point of the column: an expired card means
 * the person cannot be sent to a venue, so it reads as a warning, not as a date.
 */
function CelulaCartao({ funcionario }: { funcionario: Funcionario }) {
  const estado: EstadoCartao = funcionario.cartao_estado ?? 'sem_data';

  if (!funcionario.cartao_id && !funcionario.cartao_validade) {
    return <span className="text-tinta-400">—</span>;
  }

  const aviso = estado === 'expirado' || estado === 'a_expirar';
  const cor = estado === 'expirado'
    ? 'text-red-700 dark:text-red-300'
    : estado === 'a_expirar'
      ? 'text-amber-700 dark:text-amber-300'
      : 'text-tinta-500 dark:text-tinta-400';

  return (
    <div className="text-tinta-600 dark:text-papel-300">
      <p className="whitespace-nowrap tabular-nums">{funcionario.cartao_id ?? '—'}</p>
      <p className={`flex items-center gap-1 whitespace-nowrap text-xs tabular-nums ${cor}`}>
        {aviso && <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />}
        {funcionario.cartao_validade
          ? `Validade: ${dataCurta(funcionario.cartao_validade, true)}`
          : 'Sem validade registada'}
      </p>
      {aviso && (
        <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
          estado === 'expirado'
            ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200'
            : 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200'
        }`}>
          {estado === 'expirado' ? 'Expirado' : 'Expira em breve'}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function FormularioFuncionario({
  referencia, funcionario, aoFechar, aoGuardar, aoCriarEmpresa,
}: {
  referencia: Referencia;
  funcionario: Funcionario | null;
  aoFechar: () => void;
  aoGuardar: () => void;
  aoCriarEmpresa: (empresa: Empresa) => void;
}) {
  const { utilizador } = useAuth();
  const [form, setForm] = useState({
    nome: funcionario?.nome ?? '',
    funcao: funcionario?.funcao ?? referencia.funcoes[0]?.codigo ?? '',
    empresa_id: funcionario?.empresa_id ?? '',
    email: funcionario?.email ?? '',
    telefone: funcionario?.telefone ?? '',
    data_nascimento: funcionario?.data_nascimento ?? '',
    cartao_id: funcionario?.cartao_id ?? '',
    cartao_validade: funcionario?.cartao_validade ?? '',
    nif: funcionario?.nif ?? '',
    nib: funcionario?.nib ?? '',
    data_admissao: funcionario?.data_admissao ?? '',
    custo_hora: funcionario?.custo_hora === null || funcionario?.custo_hora === undefined
      ? '' : String(funcionario.custo_hora),
    ativo: funcionario ? Boolean(funcionario.ativo) : true,
    notas: funcionario?.notas ?? '',
  });
  const [erro, setErro] = useState<string | null>(null);
  const [aGuardar, setAGuardar] = useState(false);

  async function submeter(evento: FormEvent) {
    evento.preventDefault();
    setErro(null);
    setAGuardar(true);

    const corpo = {
      ...form,
      empresa_id: form.empresa_id || null,
      custo_hora: form.custo_hora === '' ? null : Number(form.custo_hora),
    };

    try {
      if (funcionario) await atualizar(`/funcionarios/${funcionario.id}`, corpo);
      else await criar('/funcionarios', corpo);
      aoGuardar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível guardar.');
    } finally {
      setAGuardar(false);
    }
  }

  return (
    <Modal aberto aoFechar={aoFechar} titulo={funcionario ? 'Editar Funcionário' : 'Adicionar Funcionário'}>
      <form onSubmit={submeter} className="space-y-4">
        {erro && <Erro mensagem={erro} />}

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="f-nome" className="rotulo">Nome</label>
            <input
              id="f-nome" required autoFocus className="campo" value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
            />
          </div>
          <ListaComNovo<Empresa>
            id="f-empresa-form"
            rotulo="Fornecedor"
            opcoes={referencia.empresas}
            valor={form.empresa_id}
            textoVazio="Sem fornecedor"
            aoEscolher={(id) => setForm((f) => ({ ...f, empresa_id: id }))}
            podeAdicionar={utilizador?.papel === 'admin'}
            rotuloNovo="+ Novo fornecedor"
            placeholderNovo="Nome do fornecedor"
            extra={{
              tipo: 'escolha',
              rotulo: 'Tipo',
              opcoes: [{ valor: 'agencia', texto: 'Agência' }, { valor: 'interno', texto: 'Interno' }],
            }}
            aoCriar={async (nome, tipo) => {
              const { empresa } = await criar<{ empresa: Empresa }>('/referencia/empresas', {
                nome, tipo,
              });
              return empresa;
            }}
            aoCriado={aoCriarEmpresa}
            etiqueta={(e) => (e.tipo === 'interno' ? ' (interno)' : '')}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="f-funcao" className="rotulo">Função</label>
            <select
              id="f-funcao" required className="campo" value={form.funcao}
              onChange={(e) => setForm((f) => ({ ...f, funcao: e.target.value }))}
            >
              {referencia.funcoes.map((f) => (
                <option key={f.codigo} value={f.codigo}>{f.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="f-nascimento" className="rotulo">Data de nascimento</label>
            <input
              id="f-nascimento" type="date" className="campo" value={form.data_nascimento}
              onChange={(e) => setForm((f) => ({ ...f, data_nascimento: e.target.value }))}
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="f-email" className="rotulo">Email</label>
            <input
              id="f-email" type="email" className="campo" value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="f-telefone" className="rotulo">Telefone</label>
            <input
              id="f-telefone" className="campo" value={form.telefone}
              onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))}
            />
          </div>
        </div>

        <fieldset className="rounded-lg border border-papel-300 p-3 dark:border-tinta-700">
          <legend className="px-1 text-xs font-medium uppercase tracking-wide text-tinta-500 dark:text-tinta-400">
            Cartão de identificação
          </legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="f-cartao-num" className="rotulo">Número</label>
              <input
                id="f-cartao-num" className="campo" value={form.cartao_id}
                onChange={(e) => setForm((f) => ({ ...f, cartao_id: e.target.value }))}
              />
            </div>
            <div>
              <label htmlFor="f-cartao-val" className="rotulo">Validade</label>
              <input
                id="f-cartao-val" type="date" className="campo" value={form.cartao_validade}
                onChange={(e) => setForm((f) => ({ ...f, cartao_validade: e.target.value }))}
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-tinta-500 dark:text-tinta-400">
            A lista assinala os cartões expirados e os que expiram nos próximos 30 dias.
          </p>
        </fieldset>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="f-nif" className="rotulo">NIF</label>
            <input
              id="f-nif" inputMode="numeric" className="campo" value={form.nif}
              onChange={(e) => setForm((f) => ({ ...f, nif: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="f-nib" className="rotulo">NIB / IBAN</label>
            <input
              id="f-nib" className="campo" value={form.nib}
              onChange={(e) => setForm((f) => ({ ...f, nib: e.target.value }))}
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="f-admissao" className="rotulo">Data de admissão</label>
            <input
              id="f-admissao" type="date" className="campo" value={form.data_admissao}
              onChange={(e) => setForm((f) => ({ ...f, data_admissao: e.target.value }))}
            />
          </div>
          <div>
            <label htmlFor="f-custo" className="rotulo">Valor/hora (€)</label>
            <input
              id="f-custo" type="number" min="0" step="0.01" className="campo" value={form.custo_hora}
              onChange={(e) => setForm((f) => ({ ...f, custo_hora: e.target.value }))}
            />
          </div>
        </div>

        <p className="text-xs text-tinta-500 dark:text-tinta-400">
          Sem valor/hora, os registos desta pessoa não entram no custo estimado — aparecem como
          &ldquo;por definir&rdquo; e o dashboard assinala-o.
        </p>

        <div>
          <label htmlFor="f-notas" className="rotulo">Observações</label>
          <textarea
            id="f-notas" rows={2} className="campo" value={form.notas}
            onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
          />
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-papel-300 text-marca-600 focus:ring-marca-500"
            checked={form.ativo}
            onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))}
          />
          Funcionário ativo
        </label>

        <div className="flex justify-end gap-2 border-t border-papel-200 pt-4 dark:border-tinta-700">
          <button type="button" className="btn-secundario" onClick={aoFechar} disabled={aGuardar}>
            Cancelar
          </button>
          <button type="submit" className="btn-primario" disabled={aGuardar}>
            {aGuardar && <Spinner classe="h-4 w-4" />}
            {funcionario ? 'Guardar alterações' : 'Criar funcionário'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
